import { differenceInCalendarDays, format } from 'date-fns'

import { LiveEventEvolutionEntry, LiveEventEvolutionTypeEnum } from '../../types/LiveEventCollection'
import { TrackingEvent } from '../../types/TrackingEvents'

/**
 * Pure presentation helpers for the Blueprint tab timeline
 * (`LiveEventEvolutionTimeline`). Kept out of the component so the name /
 * counting / formatting rules can be unit tested without rendering MUI.
 */

/** FIRST lists each version at its first instance, LATEST at its most recent one, ALL lists every instance. */
export type LiveEventEvolutionScope = 'first' | 'last' | 'all'

const EVOLUTION_SCOPES: readonly string[] = ['first', 'last', 'all']

/** The scope a URL asks for, or undefined when the value is missing or names no scope. */
export const resolveEvolutionScope = (value?: unknown): LiveEventEvolutionScope | undefined =>
  typeof value === 'string' && EVOLUTION_SCOPES.includes(value) ? (value as LiveEventEvolutionScope) : undefined

/**
 * Entries that have already started. The timeline never lists a scheduled version or instance,
 * so every count that describes what the timeline shows has to leave them out as well.
 */
export const filterStartedEntries = <E extends Pick<LiveEventEvolutionEntry, 'start'>>(entries: E[], now: number): E[] =>
  entries.filter((entry) => entry.start <= now)

/** Separators that can terminate a blueprint prefix shared by every version name. */
const NAME_PREFIX_SEPARATORS = [' - ', ' – ', ' — ', ': ']

export const isValidTimestamp = (timestamp: number | undefined): timestamp is number => {
  if (typeof timestamp !== 'number' || !Number.isFinite(timestamp) || timestamp <= 0) {
    return false
  }
  return !Number.isNaN(new Date(timestamp).getTime())
}

/**
 * Longest prefix shared by every name, cut back to the last separator so the
 * result is a whole blueprint name rather than a mid-word fragment.
 * Returns '' when the names have nothing meaningful in common.
 */
export const getCommonNamePrefix = (names: string[]): string => {
  const usableNames = names.filter((name) => typeof name === 'string' && name.length > 0)
  if (usableNames.length < 2 || usableNames.length !== names.length) {
    return ''
  }

  let sharedLength = usableNames[0].length
  usableNames.slice(1).forEach((name) => {
    let index = 0
    while (index < sharedLength && index < name.length && name[index] === usableNames[0][index]) {
      index++
    }
    sharedLength = index
  })

  const shared = usableNames[0].slice(0, sharedLength)

  let cut = 0
  NAME_PREFIX_SEPARATORS.forEach((separator) => {
    const index = shared.lastIndexOf(separator)
    if (index >= 0) {
      cut = Math.max(cut, index + separator.length)
    }
  })

  return shared.slice(0, cut)
}

/** Drops the shared prefix from a version name, keeping the full name when that would empty it. */
export const stripCommonNamePrefix = (name: string, prefix: string): string => {
  if (!prefix || !name.startsWith(prefix)) {
    return name
  }
  const stripped = name.slice(prefix.length).trim()
  return stripped.length > 0 ? stripped : name
}

/**
 * How many instances of an evolution entry the dialog knows about. `EvolutionTab`
 * indexes its event map both by `eventId` and by `${eventId}-${start}`, so the
 * instance keys are the ones carrying the start suffix.
 */
export const countEntryInstances = (eventKeys: string[], eventId: string): number => {
  if (!eventId) {
    return 1
  }
  const count = eventKeys.filter((key) => key.startsWith(`${eventId}-`)).length
  return count > 0 ? count : 1
}

/**
 * Whole-day length of an event, counting both the start and the end day so it
 * matches `formatEventInstanceDuration` on the Instances tab. Undefined when the
 * timestamps can't be trusted.
 */
export const getEventDurationDays = (start?: number, end?: number): number | undefined => {
  if (!isValidTimestamp(start) || !isValidTimestamp(end) || end < start) {
    return undefined
  }
  return differenceInCalendarDays(end, start) + 1
}

/** "Sep 17 – Oct 01, 2025", or undefined when the timestamps can't be trusted. */
export const formatEventDateRange = (start?: number, end?: number): string | undefined => {
  if (!isValidTimestamp(start) || !isValidTimestamp(end) || end < start) {
    return undefined
  }
  return `${format(start, 'MMM dd')} – ${format(end, 'MMM dd, yyyy')}`
}

export type EarlierVersionsSummary = {
  /** Entries in the current scope that the visible page doesn't reach. 0 when nothing is provably hidden. */
  count: number
  /** Start of the oldest hidden entry — only set when `count` is trustworthy. */
  sinceTimestamp?: number
}

/**
 * The "N earlier versions" footer only counts what we can prove is hidden:
 * entries of the current scope that fall past the visible page. Versions hidden
 * by the FIRST/LATEST scope itself are unknown to the client, so they are left
 * out rather than guessed.
 */
export const getEarlierVersionsSummary = (sortedEntries: Pick<LiveEventEvolutionEntry, 'start'>[], visibleCount: number): EarlierVersionsSummary => {
  const count = Math.max(0, sortedEntries.length - Math.max(0, visibleCount))
  if (count === 0) {
    return { count: 0 }
  }
  const oldest = sortedEntries[sortedEntries.length - 1]
  return { count, sinceTimestamp: isValidTimestamp(oldest?.start) ? oldest.start : undefined }
}

/** 1-based page holding the entry for `eventId`, so the version the dialog opened on is on screen; page 1 when it isn't listed. */
export const getPageContainingEvent = (sortedEntries: Pick<LiveEventEvolutionEntry, 'eventId'>[], eventId: string | undefined, pageSize: number): number => {
  const index = eventId ? sortedEntries.findIndex((entry) => entry.eventId === eventId) : -1
  return index < 0 ? 1 : Math.floor(index / pageSize) + 1
}

export type LastInstanceActivity = { activeNow: true } | { activeNow: false; endedAt: number }

/**
 * Whether any of the instances is running at `now`, otherwise when the most
 * recent one ended. Instances without trustworthy timestamps are skipped;
 * undefined when none is usable.
 */
export const getLastInstanceActivity = (instances: ({ start?: number; end?: number } | undefined)[], now: number): LastInstanceActivity | undefined => {
  const usable = instances.filter((instance): instance is { start: number; end: number } => {
    if (!instance || !isValidTimestamp(instance.start) || !isValidTimestamp(instance.end)) {
      return false
    }
    return instance.end >= instance.start
  })
  if (usable.some((instance) => instance.start <= now && now <= instance.end)) {
    return { activeNow: true }
  }
  const endedAt = usable.reduce<number | undefined>(
    (latest, instance) => (instance.end <= now && (latest === undefined || instance.end > latest) ? instance.end : latest),
    undefined
  )
  return endedAt === undefined ? undefined : { activeNow: false, endedAt }
}

/**
 * The backend marks the entry, not its instances, so every recurring instance arrives flagged as a mechanic change.
 * Under ALL and FIRST a row is a mechanic change only on the first instance of such an entry: later instances use the
 * updated mechanic without introducing it, and a further mechanic change always comes as its own entry.
 * Under LATEST each entry is a single row (its most recent instance, which is not the first for a recurring entry),
 * so there is no repetition to hide and the entry-level flag is kept.
 */
export const hasMechanicChange = (
  entry: Pick<LiveEventEvolutionEntry, 'firstInstance' | 'mechanicChange' | 'types'>,
  scope: LiveEventEvolutionScope
): boolean =>
  (scope === 'last' || Boolean(entry.firstInstance)) &&
  (Boolean(entry.mechanicChange) || Boolean(entry.types?.includes(LiveEventEvolutionTypeEnum.MechanicChange)))

/** Reskin is only shown for entries whose type list actually contains it. */
export const hasReskin = (entry: Pick<LiveEventEvolutionEntry, 'types'>): boolean => Boolean(entry.types?.includes(LiveEventEvolutionTypeEnum.Reskin))

export const countMechanicChangeEntries = (
  entries: Pick<LiveEventEvolutionEntry, 'firstInstance' | 'mechanicChange' | 'types'>[],
  scope: LiveEventEvolutionScope
): number => entries.filter((entry) => hasMechanicChange(entry, scope)).length

/**
 * Which tag columns the rows should reserve space for. A column is kept only
 * when at least one visible version uses it, so rows stay aligned with each
 * other without every list paying for a "Mechanic change" or "Reskin" slot it
 * never fills.
 */
export type BlueprintVersionTagSlots = {
  firstAppearance: boolean
  firstSeen: boolean
  mechanicChange: boolean
  reskin: boolean
}

/**
 * A version is NEW (the event type's first appearance) when it is the first
 * instance of a highlighted event. The evolution entry carries both flags for
 * the exact row; the tracking event of the same instance is the fallback for
 * when the entry's flags are missing. An event only counts when its start
 * matches the entry's, so a different instance of the same event can't leak in.
 */
export const isNewEntry = (entry: Pick<LiveEventEvolutionEntry, 'highlighted' | 'firstInstance' | 'start'>, event?: Partial<TrackingEvent>): boolean => {
  if (entry.highlighted && entry.firstInstance) {
    return true
  }
  return Boolean(event && event.start === entry.start && event.highlighted && event.firstInstance)
}

type TagSlotEntry = Pick<LiveEventEvolutionEntry, 'firstInstance' | 'mechanicChange' | 'types'>

export const getTagSlots = <E extends TagSlotEntry>(
  entries: E[],
  scope: LiveEventEvolutionScope,
  isNew: (entry: E) => boolean = () => false
): BlueprintVersionTagSlots => ({
  firstAppearance: entries.some(isNew),
  firstSeen: entries.some((entry) => Boolean(entry.firstInstance)),
  mechanicChange: entries.some((entry) => hasMechanicChange(entry, scope)),
  reskin: entries.some(hasReskin),
})
